import { ArrayNotEmpty, IsInt, IsNotEmpty, Max, Min, ValidateIf, ValidateNested } from 'class-validator';
import { Type } from 'class-transformer';

import { AgentStatusType, DueValue, TriggerType } from './trigger';
import { ValidateBegin } from '../validators/ValidateBegin';
import { ValidatePositive } from '../validators/ValidatePositive';
import StringMatcher from './string_matcher';

export { AgentStatusType, DueStatus, DueValue } from './trigger';

export enum AttributeType {
  Currency = 'CURRENCY',
  Date = 'DATE',
  Number = 'NUMBER',
  Percent = 'PERCENT',
  String = 'STRING',
}

export enum ConditionOperator {
  And = 'AND',
  Or = 'OR',
  Not = 'NOT',
}

export enum ConditionType {
  AgentAssigned = 'AGENT_ASSIGNED',
  AgentStatus = 'AGENT_STATUS',
  Answer = 'ANSWER',
  AppPlatformDataAttribute = 'APP_PLATFORM_DATA_ATTRIBUTE',
  BusinessHours = 'BUSINESS_HOURS',
  Channels = 'CHANNELS',
  ChatCampaign = 'CHAT_CAMPAIGN',
  ChatURL = 'CHAT_URL',
  CustomAttribute = 'CUSTOM_ATTRIBUTE',
  CustomerAssignee = 'CUSTOMER_ASSIGNEE',
  DueIn = 'DUE_IN',
  EstimatedRemainingWaitTime = 'ESTIMATED_REMAINING_WAIT_TIME',
  FirstMessageInConversation = 'FIRST_MESSAGE',
  FirstMessageInSession = 'FIRST_MESSAGE_SESSION',
  MessageBody = 'MESSAGE_BODY',
  MessageChannel = 'MESSAGE_CHANNEL',
  MessageRecipient = 'MESSAGE_RECIPIENT',
  MessageSender = 'MESSAGE_SENDER',
  MessageSubject = 'MESSAGE_SUBJECT',
  QueueItemStatus = 'QUEUE_ITEM_STATUS',
  RoutingGroup = 'ROUTING_GROUP',
  Skill = 'SKILL',
  Spam = 'SPAM',
  Topics = 'TOPICS',
  WaitTime = 'WAIT_TIME',
}

export enum CurrencyType {
  // See G10 currencies: https://pkg.go.dev/golang.org/x/text@v0.3.6/currency#Unit
  USD = 'USD', // US Dollar
  EUR = 'EUR', // Euro
  JPY = 'JPY', // Japanese Yen
  GBP = 'GBP', // Pound Sterling
  CHF = 'CHF', // Swiss Franc
  AUD = 'AUD', // Australian Dollar
  NZD = 'NZD', // New Zealand Dollar
  CAD = 'CAD', // Canadian Dollar
  SEK = 'SEK', // Swedish Krona
  NOK = 'NOK', // Norwegian Krone

  // Additional common currencies as defined by CLDR.
  BRL = 'BRL', // Brazilian Real
  CNY = 'CNY', // Chinese Yuan
  DKK = 'DKK', // Danish Krone
  INR = 'INR', // Indian Rupee
  RUB = 'RUB', // Russian Ruble
  HKD = 'HKD', // Hong Kong Dollar
  IDR = 'IDR', // Indonesian Rupiah
  KRW = 'KRW', // South Korean Won
  MXN = 'MXN', // Mexican Peso
  PLN = 'PLN', // Poland Złoty
  SAR = 'SAR', // Saudi Riyal
  THB = 'THB', // Thai Baht
  TRY = 'TRY', // Turkish Lira
  TWD = 'TWD', // New Taiwan Dollar
  ZAR = 'ZAR', // South African Rand
}

export enum EstimatedRemainingWaitTimeValueType {
  NoCapacity = 'NO_CAPACITY',
  Number = 'NUMBER',
}

export enum Excludes {
  True = 'TRUE',
  False = 'FALSE',
}

export enum EmptyInteractionType {
  Any = '',
}

export enum InteractionType {
  AbandonedCall = 'ABANDONED_CALL',
  Chat = 'CHAT',
  CustomChannel = 'CUSTOM_CHANNEL',
  Email = 'EMAIL',
  FbMessenger = 'FB_MESSENGER',
  InstagramComment = 'INSTAGRAM_COMMENT',
  InstagramDirect = 'INSTAGRAM_DIRECT',
  PhoneCall = 'PHONE_CALL',
  Sms = 'SMS',
  Task = 'TASK',
  Voicemail = 'VOICEMAIL',
  WhatsApp = 'WHATSAPP',
}

export enum NumberOperator {
  Between = 'BETWEEN',
  Equal = '=',
  GT = '>',
  GTE = '>=',
  LT = '<',
  LTE = '<=',
}

export enum QueueItemStatusType {
  Queued = 'QUEUED',
  Requeued = 'REQUEUED',
}

export enum RoutingChannel {
  Mail = 'MAIL',
  Messaging = 'MESSAGING',
  Task = 'TASK',
  Voice = 'VOICE',
}

export class AgentAssignedCondition {
  type: ConditionType;
  value: boolean;

  setValue(value: boolean) {
    this.value = value;
  }

  constructor(value = true) {
    this.type = ConditionType.AgentAssigned;
    this.value = value;
  }
}

export class AgentStatusCondition {
  type: ConditionType;
  value: AgentStatusType;

  // eslint-disable-next-line
  constructor(value = AgentStatusType.Offline) {
    this.type = ConditionType.AgentStatus;
    this.value = value;
  }

  setValue(value: AgentStatusType) {
    this.value = value;
  }
}

export class AnswerCondition {
  type: ConditionType;
  @ValidateIf(o => o.answerChannels.length === 0 && o.answerLanguages.length === 0)
  @ArrayNotEmpty({ message: 'Please select at least one answer attribute' })
  answerIds: string[];
  answerChannels: string[];
  answerLanguages: string[];

  updateAnswerId(answerId: string) {
    if (this.answerIds.length === 0) {
      this.answerIds.push(answerId);
      return;
    }

    // remove entry if exists, replace if not present
    const index = this.answerIds.indexOf(answerId);
    if (index === -1) {
      this.answerIds[0] = answerId;
    } else {
      this.answerIds = [];
    }
  }

  updateAnswerChannel(answerChannel: string) {
    // remove if present, add if not present
    const index = this.answerChannels.indexOf(answerChannel);
    index === -1 ? this.answerChannels.push(answerChannel) : this.answerChannels.splice(index, 1);
  }

  updateAnswerLanguage(answerLanguage: string) {
    // remove if present, add if not present
    const index = this.answerLanguages.indexOf(answerLanguage);
    index === -1 ? this.answerLanguages.push(answerLanguage) : this.answerLanguages.splice(index, 1);
  }

  constructor(answerIds: string[] = [], answerChannels: string[] = [], answerLanguages: string[] = []) {
    this.type = ConditionType.Answer;
    this.answerIds = answerIds;
    this.answerChannels = answerChannels;
    this.answerLanguages = answerLanguages;
  }
}

export class BusinessHoursCondition {
  type: ConditionType;
  inclusion: boolean; // true - is inside, false - is outside business hours
  @IsNotEmpty({ message: 'Please select business hours' })
  value: string;

  constructor(value: string, inclusion: boolean = true) {
    this.type = ConditionType.BusinessHours;
    this.value = value;
    this.inclusion = inclusion;
  }

  setInclusion(value: boolean) {
    this.inclusion = value;
  }

  setValue(value) {
    this.value = value;
  }
}

export class ChatCampaignCondition {
  type: ConditionType;
  @IsNotEmpty({ message: 'Please select a campaign' })
  value: string;

  constructor(value) {
    this.type = ConditionType.ChatCampaign;
    this.value = value;
  }

  setValue(value) {
    this.value = value;
  }
}

export class SpamCondition {
  type: ConditionType;

  constructor() {
    this.type = ConditionType.Spam;
  }
}

export class CustomerAssigneeCondition {
  type: ConditionType;
  value: boolean;

  constructor(value = false) {
    this.type = ConditionType.CustomerAssignee;
    this.value = value;
  }

  setValue(value) {
    this.value = value;
  }
}

export abstract class NumericalOperatorCondition {
  not: boolean;
  operator: NumberOperator;
  requirePositive: boolean;

  @ValidateBegin()
  begin: number; // valid only for BETWEEN operator

  @ValidateIf(o => o.operator === NumberOperator.Between && o.requirePositive)
  @ValidatePositive({ message: 'End must be greater than 0' })
  end: number; // valid only for BETWEEN operator

  @ValidateIf(o => o.operator !== NumberOperator.Between && o.requirePositive)
  @ValidatePositive({ message: 'Value must be greater than 0' })
  value: number; // valid for all operators except BETWEEN

  constructor(
    not: boolean = false,
    operator: NumberOperator = NumberOperator.GT,
    requirePositive: boolean = false,
    begin: number = 0,
    end: number = 0,
    value: number = 0
  ) {
    this.not = not;
    this.operator = operator;
    this.requirePositive = requirePositive;
    this.begin = begin;
    this.end = end;
    this.value = value;
  }

  getOperator(): string {
    return this.operator;
  }

  setNot(not: boolean) {
    this.not = not;
  }

  setOperator(operator: NumberOperator) {
    this.operator = operator;
    if (this.operator === NumberOperator.Between) {
      this.value = 0;
    } else {
      this.begin = 0;
      this.end = 0;
    }
  }

  setBegin(begin: number) {
    this.begin = begin;
  }

  setEnd(end: number) {
    this.end = end;
  }

  setValue(value: number) {
    this.value = value;
  }
}

export class AppPlatformDataAttributeCurrencyCondition extends NumericalOperatorCondition {
  attrId: string;
  attrType: AttributeType;
  @IsNotEmpty({ message: 'Please select currency type' })
  currency: CurrencyType;
  type: ConditionType;

  // The values below are the string representations of the begin, end, and value properties.
  // This allows for using the NumericConditionSection component to manage number display with
  // currency formatting built in, but have the original value as a string to be managed by Supernova,
  // to limit the amount of conversions needed on a floating point value. The matching type in Supernova is
  // defined in app_platform_data_attribute_currency_condition.go
  beginStr: string; // valid only for BETWEEN operator
  endStr: string; // valid only for BETWEEN operator
  valueStr: string; // valid for all operators except BETWEEN

  constructor(
    attrId: string,
    currency: CurrencyType,
    not: boolean = false,
    operator: NumberOperator = NumberOperator.GT,
    begin: number = 0,
    end: number = 0,
    value: number = 0
  ) {
    super(not, operator, false, begin, end, value);
    this.attrId = attrId;
    this.attrType = AttributeType.Currency;
    this.currency = currency;
    this.type = ConditionType.AppPlatformDataAttribute;

    this.beginStr = begin.toString();
    this.endStr = end.toString();
    this.valueStr = value.toString();
  }

  setCurrency(currency: CurrencyType) {
    this.currency = currency;
  }

  setOperator(operator: NumberOperator) {
    super.setOperator(operator);

    this.operator = operator;
    if (this.operator === NumberOperator.Between) {
      this.valueStr = '0';
    } else {
      this.beginStr = '0';
      this.endStr = '0';
    }
  }

  setBegin(begin: number) {
    super.setBegin(begin);
    this.beginStr = begin.toString();
  }

  setEnd(end: number) {
    super.setEnd(end);
    this.endStr = end.toString();
  }

  setValue(value: number) {
    super.setValue(value);
    this.valueStr = value.toString();
  }
}

export class AppPlatformDataAttributeNumberCondition extends NumericalOperatorCondition {
  attrId: string;
  attrType: AttributeType;
  type: ConditionType;

  constructor(
    attrId: string,
    not: boolean = false,
    operator: NumberOperator = NumberOperator.GT,
    begin: number = 0,
    end: number = 0,
    value: number = 0
  ) {
    super(not, operator, false, begin, end, value);
    this.attrType = AttributeType.Number;
    this.attrId = attrId;
    this.type = ConditionType.AppPlatformDataAttribute;
  }
}

export class CustomAttributeCurrencyCondition extends NumericalOperatorCondition {
  attrType: AttributeType;
  @IsNotEmpty({ message: 'Please select currency type' })
  currency: CurrencyType;
  name: string;
  type: ConditionType;

  constructor(
    name: string,
    currency: CurrencyType,
    not: boolean = false,
    operator: NumberOperator = NumberOperator.GT,
    begin: number = 0,
    end: number = 0,
    value: number = 0
  ) {
    super(not, operator, false, begin, end, value);
    this.attrType = AttributeType.Currency;
    this.currency = currency;
    this.name = name;
    this.type = ConditionType.CustomAttribute;
  }

  setCurrency(currency: CurrencyType) {
    this.currency = currency;
  }
}

export class CustomAttributeNumberCondition extends NumericalOperatorCondition {
  attrType: AttributeType;
  name: string;
  type: ConditionType;

  constructor(
    name: string,
    not: boolean = false,
    operator: NumberOperator = NumberOperator.GT,
    begin: number = 0,
    end: number = 0,
    value: number = 0
  ) {
    super(not, operator, false, begin, end, value);
    this.attrType = AttributeType.Number;
    this.name = name;
    this.type = ConditionType.CustomAttribute;
  }
}

export class DueInCondition {
  type: ConditionType;
  value: DueValue;

  constructor(value = 0) {
    this.type = ConditionType.DueIn;
    this.value = new DueValue(value);
  }

  setValue(value: DueValue) {
    this.value = value;
  }

  toJs() {
    return { ...this, value: this.value.toJs() };
  }
}

export class EstimatedRemainingWaitTimeCondition extends NumericalOperatorCondition {
  type: ConditionType;
  valueType: EstimatedRemainingWaitTimeValueType;

  @ValidateIf(
    o =>
      o.valueType === EstimatedRemainingWaitTimeValueType.Number &&
      o.operator !== NumberOperator.Between &&
      o.requirePositive
  )
  @ValidatePositive({ message: 'Value must be greater than 0' })
  value: number;

  constructor(
    begin: number = 60,
    end: number = 60,
    value: number = 60,
    operator: NumberOperator = NumberOperator.GT,
    valueType: EstimatedRemainingWaitTimeValueType = EstimatedRemainingWaitTimeValueType.Number
  ) {
    super(false, operator, true, begin, end, value);
    this.type = ConditionType.EstimatedRemainingWaitTime;
    this.value = value;
    this.valueType = valueType;
  }

  getOperator(): string {
    return this.valueType === EstimatedRemainingWaitTimeValueType.NoCapacity ? this.valueType : super.getOperator();
  }

  setOperator(operator: NumberOperator): void {
    super.setOperator(operator);
    this.valueType = EstimatedRemainingWaitTimeValueType.Number;
  }

  setValue(value: number) {
    this.value = value;
  }

  setValueType(valueType: EstimatedRemainingWaitTimeValueType): void {
    this.valueType = valueType;
    // reset numerical fields
    this.operator = NumberOperator.GT;
    this.value = 0;
    this.begin = 0;
    this.end = 0;
  }
}

export class FirstMessageInConversationCondition {
  type: ConditionType;

  constructor() {
    this.type = ConditionType.FirstMessageInConversation;
  }
}

export class FirstMessageInSessionCondition {
  type: ConditionType;

  constructor() {
    this.type = ConditionType.FirstMessageInSession;
  }
}

export class MessageChannelCondition {
  type: ConditionType;
  @ArrayNotEmpty({ message: 'Please select at least one channel' })
  value: InteractionType[];

  constructor(value = []) {
    this.type = ConditionType.MessageChannel;
    this.value = value;
  }

  updateValue(interactionType) {
    // remove if present, add if not present
    const index = this.value.indexOf(interactionType);
    index === -1 ? this.value.push(interactionType) : this.value.splice(index, 1);
  }
}

export class QueueItemStatusCondition {
  type: ConditionType;
  value: QueueItemStatusType;

  constructor(value = QueueItemStatusType.Requeued) {
    this.type = ConditionType.QueueItemStatus;
    this.value = value;
  }

  setValue(value: QueueItemStatusType) {
    this.value = value;
  }
}

export class RoutingChannelCondition {
  type: ConditionType;
  value: RoutingChannel;

  // eslint-disable-next-line
  constructor(value = RoutingChannel.Mail) {
    this.type = ConditionType.Channels;
    this.value = value;
  }

  setValue(value: RoutingChannel) {
    this.value = value;
  }

  toJs() {
    return { ...this, value: [this.value] };
  }
}

export class RoutingGroupCondition {
  type: ConditionType;
  not: boolean;
  @ArrayNotEmpty({ message: 'Please select at least one inbox' })
  value: string[];

  constructor(value: string[] = [], not: boolean = false) {
    this.type = ConditionType.RoutingGroup;
    this.not = not;
    this.value = value;
  }

  setNot(not: boolean) {
    this.not = not;
  }

  updateRoutingGroup(routingGroupId: string) {
    // remove if present, add if not present
    const index = this.value.indexOf(routingGroupId);
    index === -1 ? this.value.push(routingGroupId) : this.value.splice(index, 1);
  }
}

export class SkillCondition {
  type: ConditionType;
  operator: ConditionOperator;
  @ArrayNotEmpty({ message: 'Please select at least one skill' })
  value: string[];

  // eslint-disable-next-line
  constructor(value: string[] = [], operator: ConditionOperator = ConditionOperator.Or) {
    this.type = ConditionType.Skill;
    this.operator = operator;
    this.value = value;
  }

  setOperator(operator: ConditionOperator) {
    this.operator = operator;
  }

  updateSkill(skillId: string) {
    // remove if present, add if not present
    const index = this.value.indexOf(skillId);
    index === -1 ? this.value.push(skillId) : this.value.splice(index, 1);
  }
}

export class TopicsCondition {
  type: ConditionType;
  operator: ConditionOperator;
  @ArrayNotEmpty({ message: 'Please select at least one topic' })
  value: string[];

  // eslint-disable-next-line
  constructor(value: string[] = [], operator: ConditionOperator = ConditionOperator.Or) {
    this.type = ConditionType.Topics;
    this.operator = operator;
    this.value = value;
  }

  setOperator(operator: ConditionOperator) {
    this.operator = operator;
  }

  updateTopic(topicId: string) {
    // remove if present, add if not present
    const index = this.value.indexOf(topicId);
    index === -1 ? this.value.push(topicId) : this.value.splice(index, 1);
  }
}

export abstract class StringMatcherCondition {
  type: ConditionType;
  @ValidateNested({ each: true })
  @Type(() => StringMatcher)
  value: StringMatcher;

  constructor({ type, value }: { type: ConditionType; value?: object }) {
    const matcher = value === undefined ? new StringMatcher() : new StringMatcher(value);

    this.type = type;
    this.value = matcher;
  }

  setMatcher(value) {
    this.value = value;
  }

  toJs() {
    return { ...this, value: this.value.toJs() };
  }
}

export class AppPlatformDataAttributeStringCondition extends StringMatcherCondition {
  attrId: string;
  attrType: AttributeType;

  constructor(attrId, value?) {
    super({
      type: ConditionType.AppPlatformDataAttribute,
      value,
    });

    this.attrId = attrId;
    this.attrType = AttributeType.String;
  }
}

export class BodyCondition extends StringMatcherCondition {
  constructor(value?) {
    super({
      type: ConditionType.MessageBody,
      value,
    });
  }
}

export class ChatUrlCondition extends StringMatcherCondition {
  constructor(value?) {
    super({
      type: ConditionType.ChatURL,
      value,
    });
  }
}

export class CustomAttributeStringCondition extends StringMatcherCondition {
  attrType: AttributeType;
  name: string;

  constructor(name, value?) {
    super({
      type: ConditionType.CustomAttribute,
      value,
    });

    this.attrType = AttributeType.String;
    this.name = name;
  }
}

export class RecipientCondition extends StringMatcherCondition {
  constructor(value?) {
    super({
      type: ConditionType.MessageRecipient,
      value,
    });
  }
}

export class SenderCondition extends StringMatcherCondition {
  constructor(value?) {
    super({
      type: ConditionType.MessageSender,
      value,
    });
  }
}

export class SubjectCondition extends StringMatcherCondition {
  constructor(value?) {
    super({
      type: ConditionType.MessageSubject,
      value,
    });
  }
}

export class WaitTimeCondition {
  type: ConditionType;
  @IsInt({ message: 'Please enter a whole number of minutes' })
  @Min(1, { message: 'Enter an wait time greater than 0 minutes' })
  @Max(20160, { message: 'Wait Time cannot be more than 14 days' })
  minutes: number;
  channel: InteractionType | EmptyInteractionType;

  constructor(minutes: number = 1, channel: InteractionType | EmptyInteractionType = EmptyInteractionType.Any) {
    this.type = ConditionType.WaitTime;
    this.minutes = minutes;
    this.channel = channel;
  }

  setChannel(channel: InteractionType | EmptyInteractionType = EmptyInteractionType.Any) {
    this.channel = channel;
  }

  setMinutes(minutes: number) {
    this.minutes = minutes;
  }
}

export function conditionClassFromType(type: ConditionType, attrType?: AttributeType) {
  switch (type) {
    case ConditionType.AgentAssigned:
      return AgentAssignedCondition;
    case ConditionType.AgentStatus:
      return AgentStatusCondition;
    case ConditionType.Answer:
      return AnswerCondition;
    case ConditionType.AppPlatformDataAttribute:
      switch (attrType) {
        case AttributeType.Currency:
          return AppPlatformDataAttributeCurrencyCondition;
        case AttributeType.Number:
          return AppPlatformDataAttributeNumberCondition;
        default:
          return AppPlatformDataAttributeStringCondition;
      }
    case ConditionType.BusinessHours:
      return BusinessHoursCondition;
    case ConditionType.Channels:
      return RoutingChannelCondition;
    case ConditionType.ChatURL:
      return ChatUrlCondition;
    case ConditionType.CustomAttribute:
      switch (attrType) {
        case AttributeType.Currency:
          return CustomAttributeCurrencyCondition;
        case AttributeType.Number:
          return CustomAttributeNumberCondition;
        default:
          return CustomAttributeStringCondition;
      }
    case ConditionType.CustomerAssignee:
      return CustomerAssigneeCondition;
    case ConditionType.DueIn:
      return DueInCondition;
    case ConditionType.EstimatedRemainingWaitTime:
      return EstimatedRemainingWaitTimeCondition;
    case ConditionType.FirstMessageInConversation:
      return FirstMessageInConversationCondition;
    case ConditionType.FirstMessageInSession:
      return FirstMessageInSessionCondition;
    case ConditionType.MessageBody:
      return BodyCondition;
    case ConditionType.MessageChannel:
      return MessageChannelCondition;
    case ConditionType.MessageRecipient:
      return RecipientCondition;
    case ConditionType.MessageSender:
      return SenderCondition;
    case ConditionType.MessageSubject:
      return SubjectCondition;
    case ConditionType.ChatCampaign:
      return ChatCampaignCondition;
    case ConditionType.RoutingGroup:
      return RoutingGroupCondition;
    case ConditionType.Skill:
      return SkillCondition;
    case ConditionType.Spam:
      return SpamCondition;
    case ConditionType.Topics:
      return TopicsCondition;
    case ConditionType.WaitTime:
      return WaitTimeCondition;
    default:
      return null;
  }
}

export function conditionFromDto(dto): Condition {
  switch (dto.type) {
    case ConditionType.AgentAssigned:
      return new AgentAssignedCondition(dto.value);
    case ConditionType.AgentStatus:
      return new AgentStatusCondition(dto.value);
    case ConditionType.Answer:
      return new AnswerCondition(dto.answerIds, dto.answerChannels, dto.answerLanguages);
    case ConditionType.AppPlatformDataAttribute:
      const attrId = dto.attrId || dto.name;

      switch (dto.attrType) {
        case AttributeType.Currency:
          const dtoBegin = dto.begin || (dto.beginStr ? parseFloat(dto.beginStr) : 0);
          const dtoEnd = dto.end || (dto.endStr ? parseFloat(dto.endStr) : 0);
          const dtoValue = dto.value || (dto.valueStr ? parseFloat(dto.valueStr) : 0);
          return new AppPlatformDataAttributeCurrencyCondition(
            attrId,
            dto.currency,
            dto.not,
            dto.operator,
            dtoBegin,
            dtoEnd,
            dtoValue
          );
        case AttributeType.Number:
          return new AppPlatformDataAttributeNumberCondition(
            attrId,
            dto.not,
            dto.operator,
            dto.begin,
            dto.end,
            dto.value
          );
        default:
          return new AppPlatformDataAttributeStringCondition(attrId, dto.value);
      }
    case ConditionType.BusinessHours:
      return new BusinessHoursCondition(dto.value, !!dto.inclusion);
    case ConditionType.Channels:
      const value = Array.isArray(dto.value) ? dto.value[0] : dto.value;
      return new RoutingChannelCondition(value);
    case ConditionType.ChatCampaign:
      return new ChatCampaignCondition(dto.value);
    case ConditionType.ChatURL:
      return new ChatUrlCondition(dto.value);
    case ConditionType.CustomAttribute:
      switch (dto.attrType) {
        case AttributeType.Currency:
          return new CustomAttributeCurrencyCondition(
            dto.name,
            dto.currency,
            dto.not,
            dto.operator,
            dto.begin,
            dto.end,
            dto.value
          );
        case AttributeType.Number:
          return new CustomAttributeNumberCondition(dto.name, dto.not, dto.operator, dto.begin, dto.end, dto.value);
        default:
          return new CustomAttributeStringCondition(dto.name, dto.value);
      }
    case ConditionType.CustomerAssignee:
      return new CustomerAssigneeCondition(dto.value);
    case ConditionType.DueIn:
      return new DueInCondition(dto.value);
    case ConditionType.EstimatedRemainingWaitTime:
      return new EstimatedRemainingWaitTimeCondition(dto.begin, dto.end, dto.value, dto.operator, dto.valueType);
    case ConditionType.FirstMessageInSession:
      return new FirstMessageInSessionCondition();
    case ConditionType.MessageBody:
      return new BodyCondition(dto.value);
    case ConditionType.MessageChannel:
      return new MessageChannelCondition(dto.value);
    case ConditionType.MessageRecipient:
      return new RecipientCondition(dto.value);
    case ConditionType.MessageSender:
      return new SenderCondition(dto.value);
    case ConditionType.MessageSubject:
      return new SubjectCondition(dto.value);
    case ConditionType.QueueItemStatus:
      return new QueueItemStatusCondition(dto.value);
    case ConditionType.RoutingGroup:
      return new RoutingGroupCondition(dto.value, dto.not);
    case ConditionType.Skill:
      return new SkillCondition(dto.value, dto.operator);
    case ConditionType.Spam:
      return new SpamCondition();
    case ConditionType.Topics:
      return new TopicsCondition(dto.value, dto.operator);
    case ConditionType.WaitTime:
      return new WaitTimeCondition(dto.minutes, dto.channel);
    default:
      return new FirstMessageInConversationCondition();
  }
}

export function conditionsFromDto(dtos): Condition[] {
  return dtos.map(c => conditionFromDto(c));
}

export function convertStringMatcherConditions(conditions) {
  // only for conversion on save as the SN model differs from the view model
  conditions.forEach(condition => {
    if (!StringMatchConditions.has(condition.type)) return;
    if (
      (condition.type === ConditionType.CustomAttribute || condition.type === ConditionType.AppPlatformDataAttribute) &&
      condition.attrType !== AttributeType.String
    )
      return;
    let value = condition.value;
    if ((value.not && value.not.regex) || value.regex) return;

    if (value.not) {
      value = value.not;
    }

    let matcher = value.contains;
    if (value.exact) {
      matcher = value.exact;
    } else if (value.words) {
      matcher = value.words;
    }

    if (Array.isArray(matcher.values)) return;

    matcher.values = matcher.values
      .split(/,/)
      .map(x => x.trim())
      .filter(x => !!x);
  });
}

export const invalidConditionsForTrigger: { [key: string]: ConditionType[] } = {
  [TriggerType.AgentStatusUpdate]: [
    ConditionType.AgentStatus,
    ConditionType.AgentAssigned,
    ConditionType.Answer,
    ConditionType.BusinessHours,
    ConditionType.ChatCampaign,
    ConditionType.ChatURL,
    ConditionType.EstimatedRemainingWaitTime,
    ConditionType.FirstMessageInConversation,
    ConditionType.FirstMessageInSession,
    ConditionType.QueueItemStatus,
    ConditionType.Skill,
    ConditionType.Spam,
    ConditionType.WaitTime,
  ],
  [TriggerType.AnswerUsed]: [
    ConditionType.BusinessHours,
    ConditionType.ChatCampaign,
    ConditionType.ChatURL,
    ConditionType.EstimatedRemainingWaitTime,
    ConditionType.FirstMessageInConversation,
    ConditionType.FirstMessageInSession,
    ConditionType.QueueItemStatus,
    ConditionType.Skill,
    ConditionType.Spam,
    ConditionType.WaitTime,
  ],
  [TriggerType.ConversationDueIn]: [
    ConditionType.Answer,
    ConditionType.BusinessHours,
    ConditionType.ChatCampaign,
    ConditionType.ChatURL,
    ConditionType.DueIn,
    ConditionType.EstimatedRemainingWaitTime,
    ConditionType.FirstMessageInConversation,
    ConditionType.FirstMessageInSession,
    ConditionType.QueueItemStatus,
    ConditionType.Skill,
    ConditionType.Spam,
    ConditionType.WaitTime,
  ],
  [TriggerType.IncomingCommunication]: [
    ConditionType.Answer,
    ConditionType.Channels,
    ConditionType.QueueItemStatus,
    ConditionType.Skill,
    ConditionType.WaitTime,
  ],
  [TriggerType.SmartMatch]: [
    ConditionType.AgentAssigned,
    ConditionType.AgentStatus,
    ConditionType.Answer,
    ConditionType.BusinessHours,
    ConditionType.Channels,
    ConditionType.ChatCampaign,
    ConditionType.ChatURL,
    ConditionType.EstimatedRemainingWaitTime,
    ConditionType.FirstMessageInConversation,
    ConditionType.FirstMessageInSession,
    ConditionType.MessageBody,
    ConditionType.MessageRecipient,
    ConditionType.MessageSender,
    ConditionType.MessageSubject,
    ConditionType.Spam,
  ],
  [TriggerType.TaskDueIn]: [
    ConditionType.AgentAssigned,
    ConditionType.Answer,
    ConditionType.BusinessHours,
    ConditionType.Channels,
    ConditionType.ChatCampaign,
    ConditionType.ChatURL,
    ConditionType.DueIn,
    ConditionType.EstimatedRemainingWaitTime,
    ConditionType.FirstMessageInConversation,
    ConditionType.FirstMessageInSession,
    ConditionType.MessageChannel,
    ConditionType.MessageRecipient,
    ConditionType.MessageSender,
    ConditionType.MessageSubject,
    ConditionType.QueueItemStatus,
    ConditionType.Skill,
    ConditionType.Spam,
    ConditionType.Topics,
    ConditionType.WaitTime,
  ],
  [TriggerType.TopicsAdded]: [
    ConditionType.Answer,
    ConditionType.BusinessHours,
    ConditionType.ChatCampaign,
    ConditionType.ChatURL,
    ConditionType.EstimatedRemainingWaitTime,
    ConditionType.FirstMessageInConversation,
    ConditionType.FirstMessageInSession,
    ConditionType.QueueItemStatus,
    ConditionType.Skill,
    ConditionType.Spam,
    ConditionType.WaitTime,
  ],
};

const StringMatchConditions = new Set([
  ConditionType.AppPlatformDataAttribute,
  ConditionType.ChatURL,
  ConditionType.CustomAttribute,
  ConditionType.MessageBody,
  ConditionType.MessageRecipient,
  ConditionType.MessageSender,
  ConditionType.MessageSubject,
]);

type Condition =
  | AgentAssignedCondition
  | AgentStatusCondition
  | AnswerCondition
  | AppPlatformDataAttributeCurrencyCondition
  | AppPlatformDataAttributeNumberCondition
  | AppPlatformDataAttributeStringCondition
  | BodyCondition
  | BusinessHoursCondition
  | ChatCampaignCondition
  | ChatUrlCondition
  | CustomerAssigneeCondition
  | CustomAttributeCurrencyCondition
  | CustomAttributeNumberCondition
  | CustomAttributeStringCondition
  | DueInCondition
  | EstimatedRemainingWaitTimeCondition
  | FirstMessageInConversationCondition
  | FirstMessageInSessionCondition
  | MessageChannelCondition
  | QueueItemStatusCondition
  | RecipientCondition
  | RoutingChannelCondition
  | RoutingGroupCondition
  | SkillCondition
  | SenderCondition
  | SpamCondition
  | SubjectCondition
  | TopicsCondition
  | WaitTimeCondition;

export default Condition;
